import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowRight, Award, KanbanSquare, TrendingUp, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { EmptyStateIllustration } from '@/components/ui/empty-state-illustration';
import { PageHeader } from '@/components/ui/page-header';
import { Skeleton } from '@/components/ui/skeleton';
import { usePipelines } from '@/hooks/queries/useCrmPipelines';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import {
  useDashboardCommercial,
  useForecasting,
  useOpportunitesUrgentes,
  useRecentWonLost,
} from '@/hooks/queries/useCrmDashboard';
import { useOpportunites } from '@/hooks/queries/useCrmOpportunites';
import { formatDA, formatDateDZ } from '@/lib/format';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';

export function CrmDashboardPage(): JSX.Element {
  const { data: entreprises = [] } = useEntreprises();
  const [entrepriseId, setEntrepriseId] = useState('');
  useEffect(() => {
    if (!entrepriseId && entreprises[0]) setEntrepriseId(entreprises[0].id);
  }, [entreprises, entrepriseId]);

  const { data: pipelines = [] } = usePipelines(entrepriseId);
  const { data: openOpps, isLoading: oppsLoading } = useOpportunites({
    entrepriseId,
    statut: 'open',
    pageSize: 1,
  });
  const { data: forecast = [] } = useForecasting({ entrepriseId });
  const { data: dashboard = [] } = useDashboardCommercial(entrepriseId);
  const { data: urgents = [] } = useOpportunitesUrgentes(entrepriseId);
  const { data: recents = [] } = useRecentWonLost(entrepriseId);

  const noOpenOpps = (openOpps?.count ?? 0) === 0;

  const ca = useMemo(() => {
    const result = { 30: 0, 60: 0, 90: 0 };
    for (const f of forecast) {
      if (f.horizon_jours === 30) result[30] += Number(f.ca_pondere);
      if (f.horizon_jours === 60) result[60] += Number(f.ca_pondere);
      if (f.horizon_jours === 90) result[90] += Number(f.ca_pondere);
    }
    return result;
  }, [forecast]);

  const topCommercial = useMemo(() => {
    if (dashboard.length === 0) return null;
    return dashboard.slice().sort((a, b) => Number(b.ca_won_mois) - Number(a.ca_won_mois))[0];
  }, [dashboard]);

  const tauxConv = useMemo(() => {
    if (dashboard.length === 0) return 0;
    const total = dashboard.reduce(
      (acc, d) => ({ won: acc.won + Number(d.nb_won_90j), lost: acc.lost + Number(d.nb_lost_90j) }),
      { won: 0, lost: 0 },
    );
    if (total.won + total.lost === 0) return 0;
    return Math.round((100 * total.won) / (total.won + total.lost));
  }, [dashboard]);

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title={t('crm.title')}
        subtitle={t('crm.subtitle')}
        actions={
          <select
            value={entrepriseId}
            onChange={(e) => setEntrepriseId(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            {entreprises.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nom}
              </option>
            ))}
          </select>
        }
      />

      {entrepriseId && pipelines.length === 0 ? (
        <EmptyState
          icon={<KanbanSquare className="h-6 w-6" />}
          title={`Aucun pipeline configuré pour ${entreprises.find((e) => e.id === entrepriseId)?.nom ?? 'cette entreprise'}`}
          description="Créez votre premier pipeline pour commencer à suivre vos opportunités commerciales."
          action={
            <Button asChild>
              <Link to={`/parametres/crm/pipelines?entreprise=${entrepriseId}`}>
                → Configurer un pipeline
              </Link>
            </Button>
          }
        />
      ) : oppsLoading && noOpenOpps ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-lg" />
          ))}
        </div>
      ) : noOpenOpps && dashboard.length === 0 ? (
        <EmptyStateIllustration
          type="opportunites"
          title="Aucune opportunité ouverte"
          description="Le CRM est vide. Créez une opportunité pour la voir apparaître dans le Kanban et le tableau de bord."
          action={
            <Button asChild size="sm">
              <Link to="/crm/liste">Créer une opportunité</Link>
            </Button>
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <KpiCard
              icon={<Users className="h-4 w-4" />}
              label={t('crm.kpi.totalOpen')}
              value={(openOpps?.count ?? 0).toString()}
            />
            <KpiCard
              icon={<TrendingUp className="h-4 w-4" />}
              label={t('crm.kpi.ca30j')}
              value={formatDA(ca[30])}
            />
            <KpiCard
              icon={<TrendingUp className="h-4 w-4" />}
              label={t('crm.kpi.ca90j')}
              value={formatDA(ca[90])}
            />
            <KpiCard
              icon={<Award className="h-4 w-4" />}
              label={t('crm.kpi.tauxConv')}
              value={`${tauxConv} %`}
              subtext={
                topCommercial
                  ? `Top : ${topCommercial.full_name ?? '—'} (${formatDA(Number(topCommercial.ca_won_mois))})`
                  : undefined
              }
            />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <section className="rounded-lg border border-border bg-card p-4">
              <header className="mb-3 flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-sm font-semibold">
                  <AlertCircle className="h-4 w-4 text-warning" />
                  Opportunités urgentes (&gt; 14j sans activité)
                </h3>
                <span className="text-xs text-muted-foreground">{urgents.length}</span>
              </header>
              {urgents.length === 0 ? (
                <p className="text-xs text-muted-foreground">Tout est à jour 🎉</p>
              ) : (
                <ul className="scrollbar-thin max-h-72 space-y-2 overflow-y-auto">
                  {urgents.slice(0, 20).map((u) => (
                    <li
                      key={u.id}
                      className="flex items-center justify-between gap-2 rounded-md border border-border/50 px-3 py-2 text-xs"
                    >
                      <div className="min-w-0 flex-1">
                        <Link
                          to={`/crm/opportunite/${u.id}`}
                          className="line-clamp-1 font-medium hover:underline"
                        >
                          {u.titre}
                        </Link>
                        <div className="text-[10px] text-muted-foreground">
                          {u.reference} · {u.etape_nom}
                        </div>
                      </div>
                      <span
                        className={cn(
                          'num shrink-0 text-xs',
                          u.jours_sans_activite > 30 ? 'text-destructive' : 'text-warning',
                        )}
                      >
                        {u.jours_sans_activite}j
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="rounded-lg border border-border bg-card p-4">
              <header className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold">Récents gagnés / perdus (7j)</h3>
                <Link
                  to="/crm/liste"
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  Voir tout <ArrowRight className="h-3 w-3" />
                </Link>
              </header>
              {recents.length === 0 ? (
                <p className="text-xs text-muted-foreground">Aucun mouvement sur 7j</p>
              ) : (
                <ul className="scrollbar-thin max-h-72 space-y-2 overflow-y-auto">
                  {recents.map((o) => (
                    <li
                      key={o.id}
                      className="flex items-center justify-between gap-2 rounded-md border border-border/50 px-3 py-2 text-xs"
                    >
                      <div className="min-w-0 flex-1">
                        <Link
                          to={`/crm/opportunite/${o.id}`}
                          className="line-clamp-1 font-medium hover:underline"
                        >
                          {o.titre}
                        </Link>
                        <div className="text-[10px] text-muted-foreground">
                          {o.reference} ·{' '}
                          {o.date_cloture_reelle ? formatDateDZ(o.date_cloture_reelle) : '—'}
                        </div>
                      </div>
                      <Badge variant={o.statut === 'won' ? 'success' : 'destructive'}>
                        {t(`crm.statut.${o.statut}`)}
                      </Badge>
                      <span className="num shrink-0 text-xs font-medium">
                        {formatDA(Number(o.montant_estime_dzd))}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          <section className="rounded-lg border border-border bg-card p-4">
            <h3 className="mb-3 text-sm font-semibold">Performance par commercial (90j)</h3>
            <div className="scrollbar-thin overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="py-2">Commercial</th>
                    <th className="py-2 text-right">Opp. ouvertes</th>
                    <th className="py-2 text-right">CA pondéré</th>
                    <th className="py-2 text-right">CA gagné mois</th>
                    <th className="py-2 text-right">CA gagné 90j</th>
                    <th className="py-2 text-right">Taux conv.</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.map((d) => (
                    <tr key={d.commercial_id} className="border-b border-border/40">
                      <td className="py-2">{d.full_name ?? d.commercial_id.slice(0, 8)}</td>
                      <td className="num py-2 text-right">{d.nb_open}</td>
                      <td className="num py-2 text-right">{formatDA(Number(d.ca_pondere_open))}</td>
                      <td className="num py-2 text-right">{formatDA(Number(d.ca_won_mois))}</td>
                      <td className="num py-2 text-right">{formatDA(Number(d.ca_won_90j))}</td>
                      <td className="num py-2 text-right">{Number(d.taux_conversion_90j)} %</td>
                    </tr>
                  ))}
                  {dashboard.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-4 text-center text-muted-foreground">
                        Aucune donnée pour cette entreprise.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <div className="flex items-center justify-end">
            <Button asChild variant="outline">
              <Link to="/crm/kanban">Vue Kanban →</Link>
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  subtext,
}: {
  icon: JSX.Element;
  label: string;
  value: string;
  subtext?: string;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="num text-xl font-semibold">{value}</div>
      {subtext && <div className="line-clamp-1 text-[10px] text-muted-foreground">{subtext}</div>}
    </div>
  );
}
